import type { AdminOrderStatus } from '@carcheck/shared';

/**
 * The order statuses an admin may force-cancel from (AUT-123, architect
 * guardrail #3). Force-cancel is meaningless / a guaranteed 409 once an
 * order is `completed` / `released` / `cancelled` / `expired`, so the
 * footer button is hidden in those states.
 *
 * This is PURE UX — the backend (`forceCancelByAdmin`) is the sole
 * authority and returns 409 regardless of what the SPA shows. Gating the
 * button just spares the admin a guaranteed-failure round-trip.
 *
 * Typed as a `ReadonlySet<AdminOrderStatus>` so adding / renaming a status
 * in `@carcheck/shared` surfaces here at typecheck rather than silently
 * shipping a stale gate.
 */
export const ADMIN_FORCE_CANCELLABLE: ReadonlySet<AdminOrderStatus> = new Set<AdminOrderStatus>([
  'draft',
  'awaiting_payment',
  'paid',
  'accepted',
  'in_progress',
  'report_submitted',
  'disputed',
]);

/** True when the order is in a state an admin can force-cancel. */
export const isForceCancellable = (status: AdminOrderStatus): boolean =>
  ADMIN_FORCE_CANCELLABLE.has(status);
